import React, { useMemo, useState } from 'react';
import { useTable, useSortBy, useFlexLayout } from 'react-table'
import styled from 'styled-components';
import { Table, HeaderRow, TableCell, ColumnHeaderEdge, TableRow } from '../Shared/Tables';
import PackageRowComponent from './PackageRowComponent'
import PillHeaderComponent from '../Shared/PillHeaderComponent';
import UnconfirmedEmailComponent from '../Shared/UnconfirmedEmailComponent';
import { PackageDetailsSummary } from '../../../types';
import moment from 'moment';
import PageTitle from '../Shared/PageTitle';
import { useTranslation } from 'react-i18next';
import withSuspense from '../Shared/withSuspense';

const TableBody = styled.tbody`
    ${p => p.isCollapsed ? 'visibility: collapse;' : ''}
    white-space: nowrap;
    border: none;
`;

const PackageHeaderRow = styled(HeaderRow)`
    th {
        border-right: 1px solid white;
        border-left: none;
    }
`;

const PackageTableWrapper = styled.div`
    border: 1px solid #D5D7D7;
    overflow-y: hidden;
`;

const PackageTable = styled(Table)`

`;



const TableSectionTitleBody = styled(TableBody)`
    td {
        padding: 0px !important;
        text-align: initial !important;
    }
`;

const TableSectionTitle = ({ children, colSpan }) => {
    return (
        <TableSectionTitleBody>
            <TableRow>
                <TableCell colSpan={colSpan}>
                    {children}
                </TableCell>
            </TableRow>
        </TableSectionTitleBody>
    );
}

interface PackageTableProps {
    pendingPackages: PackageDetailsSummary[];
    completePackages: PackageDetailsSummary[];
    isConsignmentMode: boolean;
    customDataNames: string[];
    isUserAuthenticated: boolean;
    emailConfirmed: boolean;
}

const PackageTableComponent = ({
    pendingPackages, 
    completePackages, 
    isConsignmentMode, 
    customDataNames, 
    isUserAuthenticated, 
    emailConfirmed 
}: PackageTableProps) => {
    const [isPendingCollapsed, setIsPendingCollapsed] = useState(false);
    const [isCompleteCollapsed, setIsCompleteCollapsed] = useState(false);

    const [t] = useTranslation();

    let columnArray = [
        {
            Header: t('mypackage_tracking_ref'),
            accessor: 'TrackingReference',
            Cell: e => 
                <a href={"Package/Details?trackingNumber=" + e.value}>
                    {e.value}
                </a>
        },
        /* Remove this until functionality to change friendly name is added
        {
            Header: t('favorite_friendly_name'),
            accessor: 'FriendlyName',
            Cell: e => 
                <a href={"Package/Details?trackingNumber=" + e.row.original.TrackingReference}>
                    {e.value}
                </a>
        },
        */
        {
            Header: t('mypackage_status'),
            accessor: 'Status',
            Cell: e => 
                <a href={"Package/Details?trackingNumber=" + e.row.original.TrackingReference}>
                    {e.value}
                </a>
        }
    ];

    // Custom columns
    customDataNames.map(name => {
        columnArray.push({
            Header: name,
            accessor: `JobCustomDataDict[${name}]`,
            Cell: e =>
                <a href={"Package/Details?trackingNumber=" + e.row.original.TrackingReference}>
                    {e.value}
                </a>
        });
    });

    // Items column in consignment mode
    if(isConsignmentMode)
    {
        columnArray.push({
            Header: t('mypackage_items'),
            accessor: 'Items',
            Cell: e => 
                <a href={"Package/Details?trackingNumber=" + e.row.original.TrackingReference}>
                    {e.value}
                </a>
        });
    }

    // Last updated column at the end
    columnArray.push({
        Header: t('mypackage_last_update'),
        accessor: 'LastUpdated',
        Cell: e => 
            <a href={"Package/Details?trackingNumber=" + e.row.original.TrackingReference}>
                {moment(e.value).toDate().toLocaleString()}
            </a>
    });


    const columns = useMemo(() => columnArray, []);

    /*
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        allColumns,
        toggleHideAllColumns,
        getToggleHideAllColumnsProps,
    } = useTable({ columns, data: packages, manualSortBy: false }, useSortBy);
    */

    const pending = useTable({ columns, data: pendingPackages, manualSortBy: false }, useSortBy, useFlexLayout);
    const complete = useTable({ columns, data: completePackages }, useFlexLayout);

    

    return (
        <>
            <PageTitle>{t('package_detail_title')}</PageTitle>
            
            {isUserAuthenticated && !emailConfirmed &&
                <UnconfirmedEmailComponent />}
            
            <PackageTableWrapper className={'table-responsive'}>
                <PackageTable id={'package-list'} className={'table-sm table-hover rows'} {...pending.getTableProps()}>
                    <thead>
                        <PackageHeaderRow>
                            {pending.headerGroups.map(headerGroup => (
                                    headerGroup.headers.map(column =>
                                        <ColumnHeaderEdge {...column.getHeaderProps(column.getSortByToggleProps())}>
                                            {column.render('Header')}
                                        </ColumnHeaderEdge>
                                    )
                                ))}
                        </PackageHeaderRow>
                    </thead>

                    {pendingPackages.length > 0 && 
                    <>
                        <TableSectionTitle colSpan={columns.length}>
                            <PillHeaderComponent 
                                title={t('package_detail_pending_headers')} 
                                isCollapsible={true} 
                                isCollapsed={isPendingCollapsed} 
                                toggleCollapse={() => setIsPendingCollapsed(!isPendingCollapsed)} />
                        </TableSectionTitle>

                        {<TableBody isCollapsed={isPendingCollapsed} {...pending.getTableBodyProps()}>
                            {
                                pending.rows.map((row) => {
                                    pending.prepareRow(row);
                                    let packageRow = row.original;
                                    return (
                                        <PackageRowComponent key={packageRow.TrackingReference} packageSummary={packageRow} handleClick={() => {}} >
                                            {row.cells.map(cell => {
                                                return <TableCell {...cell.getCellProps()}>
                                                    {cell.render('Cell')}
                                                </TableCell>
                                            })}
                                        </PackageRowComponent>
                                    )
                                })
                            }
                        </TableBody>}
                    </>}

                    {completePackages.length > 0 &&
                    <>
                        <TableSectionTitle colSpan={columns.length}>
                            <PillHeaderComponent 
                                title={t('package_detail_complete_headers')} 
                                isCollapsible={true} 
                                isCollapsed={isCompleteCollapsed} 
                                toggleCollapse={() => setIsCompleteCollapsed(!isCompleteCollapsed)} />
                        </TableSectionTitle>

                        {<TableBody isCollapsed={isCompleteCollapsed} {...complete.getTableBodyProps()}>
                            {
                                complete.rows.map((row) => {
                                    complete.prepareRow(row);
                                    let packageRow = row.original;
                                    return (
                                        <PackageRowComponent key={packageRow.TrackingReference} packageSummary={packageRow} handleClick={() => {}} >
                                            {row.cells.map(cell => {
                                                return <TableCell {...cell.getCellProps()}>
                                                    {cell.render('Cell')}
                                                </TableCell>
                                            })}
                                        </PackageRowComponent>
                                    )
                                })
                            }
                        </TableBody>}
                    </>}

                    {pendingPackages.length == 0 && completePackages.length == 0 &&
                        <TableSectionTitle colSpan={columns.length}>{t('package_detail_no_packages')}</TableSectionTitle>
                    }
                </PackageTable>
            </PackageTableWrapper>
            
        </>
    );
};

export default withSuspense(PackageTableComponent);